/**
 * Best-effort extraction of a short, user-facing reason string from an
 * error thrown by the connector fetcher. Designed to be safe to call on
 * arbitrary `unknown` values — never throws.
 *
 * The fetcher in `connector-fetcher.ts` wraps server-side error bodies
 * by stashing the parsed JSON onto `Error.stack` (the runtime value is
 * the original object, only the TS cast is `string`). The connector
 * exception middleware ships errors as `{ StatusCode, Message, Detailed }`,
 * with `Detailed` carrying the actual exception message — that's what we
 * surface here so the UI can show a hint like "Files not found for ..." or
 * "Synthetic download failure (...)".
 */
const MAX_REASON_LENGTH = 160;

const GENERIC_PHRASES = new Set([
    'network error',
    'an error occurred while processing your request.',
]);

function pick(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return undefined;
    }
    if (GENERIC_PHRASES.has(trimmed.toLowerCase())) {
        return undefined;
    }
    return trimmed.length > MAX_REASON_LENGTH
        ? `${trimmed.slice(0, MAX_REASON_LENGTH - 3)}...`
        : trimmed;
}

export function extractConnectorErrorReason(
    error: unknown
): string | undefined {
    if (!error) {
        return undefined;
    }
    if (typeof error === 'string') {
        return pick(error);
    }
    if (typeof error !== 'object') {
        return undefined;
    }

    const obj = error as Record<string, unknown>;

    // The fetcher stashes the parsed server body on `.stack` (typed as
    // string but actually the parsed object at runtime).
    const inner =
        obj.stack && typeof obj.stack === 'object'
            ? (obj.stack as Record<string, unknown>)
            : undefined;

    const candidates: unknown[] = [
        inner?.Detailed,
        inner?.detailed,
        inner?.detail,
        inner?.Message,
        inner?.message,
        inner?.title,
        inner?.payload,
        obj.Detailed,
        obj.detailed,
        obj.detail,
        obj.payload,
        obj.message,
    ];

    for (const candidate of candidates) {
        const picked = pick(candidate);
        if (picked) {
            return picked;
        }
    }
    return undefined;
}
